import { createFileRoute, Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { ArrowUpRight, CalendarClock, Crown, Wallet } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import {
  PlanCheckout,
  PlanCheckoutEmpty,
  PlanCheckoutSpinner,
  usePlanOrder,
} from "@/components/subscription/plan-checkout";
import { PlanGrid } from "@/components/subscription/plan-grid";
import { PlanUpgradeDialog } from "@/components/subscription/upgrade-dialog";
import { useQuotaStatus } from "@/hooks/use-quota-status";
import {
  createPlanOrder,
  createPlanUpgradeOrder,
  type SubscriptionPlan,
  type UserSubscription,
} from "@/lib/boide-api";

export const Route = createFileRoute("/_authenticated/goi")({
  head: () => ({
    meta: [
      { title: "Gói Quota Ngày - Hạn Mức Xem Bói Mỗi Ngày" },
      {
        name: "description",
        content:
          "Mua gói quota ngày để có hạn mức xem bói mỗi ngày với chi phí tiết kiệm hơn nạp lẻ. Xem bảng giá và hạn mức còn lại hôm nay.",
      },
    ],
  }),
  component: GoiPage,
});

function formatMoney(amount?: number | null) {
  return `${Math.max(0, Math.round(Number(amount) || 0)).toLocaleString("vi-VN")}đ`;
}

/** `2026-09-27` → `27/09/2026`. Ngày từ BE đã là ngày lịch VN, không đổi múi giờ. */
function formatDate(value?: string | null) {
  if (!value) return "--";
  const [y, m, d] = value.split("-");
  return d && m && y ? `${d}/${m}/${y}` : value;
}

function GoiPage() {
  const quota = useQuotaStatus();
  const order = usePlanOrder();
  const [upgradeTarget, setUpgradeTarget] = useState<UserSubscription | null>(null);
  const checkoutRef = useRef<HTMLElement>(null);

  const subscriptions = quota.data?.subscriptions ?? [];
  const limit = Math.max(0, Number(quota.data?.quota_limit) || 0);
  const remaining = Math.max(0, Number(quota.data?.remaining) || 0);
  const used = Math.max(0, Number(quota.data?.used_amount) || 0);
  const percent = limit > 0 ? Math.max(0, Math.min(100, (remaining / limit) * 100)) : 0;
  // Gói nào đang cho hạn mức cao nhất thì đứng tên; xem `planLabelOf` ở QuotaRow.
  const ownedSlugs = new Set(subscriptions.map((sub) => sub.plan_slug));

  const scrollToCheckout = () => {
    // Đợi một nhịp cho khối QR mount rồi mới cuộn — cuộn trước là cuộn tới chỗ trống.
    window.setTimeout(
      () => checkoutRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      80,
    );
  };

  const buyPlan = async (plan: SubscriptionPlan) => {
    const created = await order.start(() => createPlanOrder(plan.slug), { plan_slug: plan.slug });
    if (created) scrollToCheckout();
  };

  const payUpgrade = async (planSlug: string) => {
    const sub = upgradeTarget;
    if (!sub) return;
    const created = await order.start(() => createPlanUpgradeOrder(sub.id, planSlug), {
      plan_slug: planSlug,
      upgrade_subscription_id: sub.id,
    });
    if (created) {
      setUpgradeTarget(null);
      scrollToCheckout();
    }
  };

  return (
    <main className="app-feature-page flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-4xl px-3 py-6 pb-24 md:py-10">
        <header className="mb-6">
          <h1 className="font-display text-2xl text-foreground md:text-3xl">Gói quota ngày</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Gói cấp cho bạn một <strong className="text-foreground">hạn mức tiêu mỗi ngày</strong>,
            làm mới lúc 0h giờ Việt Nam. Hạn mức được dùng trước, hết mới trừ tới ví — nên số dư ví
            không giảm khi bạn còn quota.
          </p>
        </header>

        {/* ── Hạn mức hôm nay ───────────────────────────────────────────── */}
        <section className="rounded-3xl border border-gold-soft/40 bg-card/70 p-5 shadow-mystic backdrop-blur-xl md:p-6">
          {quota.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-10 w-56" />
              <Skeleton className="h-2 w-full" />
            </div>
          ) : quota.isError ? (
            <p className="text-sm text-destructive">
              Chưa đọc được hạn mức hôm nay. Vui lòng tải lại trang.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-[0.2em] text-primary/80">
                    Hạn mức hôm nay
                  </p>
                  <p className="mt-1.5 font-display text-3xl font-bold tabular-nums text-primary">
                    {formatMoney(remaining)}
                    <span className="ml-1.5 font-sans text-sm font-medium text-muted-foreground">
                      / {formatMoney(limit)}
                    </span>
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Đã dùng {formatMoney(used)} trong ngày {formatDate(quota.data?.quota_date)}
                  </p>
                </div>
                <div className="rounded-2xl border border-gold-soft/40 bg-background/40 px-3.5 py-2.5 text-right">
                  <p className="flex items-center justify-end gap-1.5 text-[11px] text-muted-foreground">
                    <Wallet className="size-3" /> Ví bói
                  </p>
                  <p className="mt-0.5 font-semibold tabular-nums text-foreground">
                    {formatMoney(quota.data?.wallet_amount)}
                  </p>
                  {/* Con số quyết định "bói được hay không" = quota còn lại + ví.
                      Hiện nó ra để người có gói không nhìn ví 0đ rồi tưởng hết tiền. */}
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Tiêu được:{" "}
                    <strong className="font-semibold text-primary">
                      {formatMoney(quota.data?.spendable)}
                    </strong>
                  </p>
                </div>
              </div>
              <span
                className="mt-3 block h-1.5 overflow-hidden rounded-full bg-primary/12"
                aria-hidden="true"
              >
                <span
                  className="block h-full rounded-full bg-primary transition-[width]"
                  style={{ width: `${percent}%` }}
                />
              </span>
            </>
          )}
        </section>

        {/* ── Gói đang chạy ─────────────────────────────────────────────── */}
        <section className="mt-6">
          <h2 className="font-display text-lg text-foreground">Gói đang chạy</h2>
          {quota.isLoading ? (
            <Skeleton className="mt-3 h-24 w-full rounded-3xl" />
          ) : !subscriptions.length ? (
            <p className="mt-3 rounded-3xl border border-dashed border-gold-soft/40 bg-background/30 px-4 py-6 text-center text-sm text-muted-foreground">
              Bạn chưa có gói nào. Chọn một gói bên dưới để có hạn mức mỗi ngày.
            </p>
          ) : (
            <div className="mt-3 space-y-3">
              {/* Nhiều đoạn ĐƯỢC PHÉP chồng nhau — hạn mức ngày là TỔNG của chúng,
                  nên liệt kê từng đoạn thay vì gộp thành "gói của tôi" số ít. */}
              {subscriptions.map((sub) => (
                <article
                  key={sub.id}
                  className="rounded-3xl border border-gold-soft/40 bg-card/70 p-4 shadow-sm backdrop-blur-xl"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 font-semibold text-foreground">
                        <Crown className="size-4 shrink-0 text-primary" />
                        <span className="truncate">{sub.plan_name}</span>
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        <strong className="text-primary">{formatMoney(sub.daily_quota)}</strong>
                        /ngày
                      </p>
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                        <CalendarClock className="size-3" />
                        {formatDate(sub.starts_on)} → {formatDate(sub.ends_on)} · còn{" "}
                        <strong className="text-foreground">{sub.days_left} ngày</strong>
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setUpgradeTarget(sub)}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-gold-soft/50 bg-background/40 px-3.5 py-2 text-xs font-semibold text-primary transition hover:border-primary/60"
                    >
                      <ArrowUpRight className="size-3.5" />
                      Nâng cấp
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* ── Bảng giá ──────────────────────────────────────────────────── */}
        <section className="mt-8">
          <h2 className="font-display text-lg text-foreground">Bảng giá gói</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Mua thêm gói khi đang có gói cũng được — hạn mức mỗi ngày của các gói{" "}
            <strong className="text-foreground">cộng dồn</strong>.
          </p>
          <PlanGrid
            className="mt-3"
            ownedSlugs={ownedSlugs}
            buying={order.creating}
            onBuy={(plan) => void buyPlan(plan)}
          />
        </section>

        {/* ── Thanh toán ────────────────────────────────────────────────── */}
        <section
          ref={checkoutRef}
          className="mt-8 rounded-3xl border border-gold-soft/40 bg-card/70 p-5 shadow-mystic backdrop-blur-xl md:p-6"
        >
          {order.creating && !order.order ? (
            <PlanCheckoutSpinner />
          ) : order.order ? (
            <PlanCheckout
              order={order.order}
              isPaid={order.isPaid}
              checking={order.checking}
              onCheck={() => void order.check()}
              onReset={order.reset}
            />
          ) : (
            <PlanCheckoutEmpty />
          )}
          {order.error ? <p className="mt-3 text-sm text-destructive">{order.error}</p> : null}
        </section>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Cần thêm tiền lẻ ngoài hạn mức gói?{" "}
          <Link to="/thanh-toan" className="text-primary underline">
            Nạp vào ví bói
          </Link>
          .
        </p>
      </div>

      <PlanUpgradeDialog
        subscription={upgradeTarget}
        open={!!upgradeTarget}
        onOpenChange={(next) => {
          if (!next) setUpgradeTarget(null);
        }}
        paying={order.creating}
        onPick={(option) => void payUpgrade(option.plan_slug)}
      />
    </main>
  );
}
